/**
 * Lazy model registry to break circular imports:
 * Model -> attributes -> FkEntityAttribute -> Model (TDZ).
 *
 * FK attributes resolve models on first use via getModel(), not at module load.
 */

const registry = new Map();
let modelsLoadStarted = false;

export function registerModel(name, modelClass) {
  if (name && modelClass) {
    registry.set(name, modelClass);
  }
}

/**
 * Returns a loader marker for FkBaseAttribute — does not resolve the model yet.
 */
export function fkModel(name) {
  const loader = () => getModel(name);
  loader.__isFkModelLoader = true;
  return loader;
}

export function getModel(name) {
  ensureModelsRegistered();
  const modelClass = registry.get(name);
  if (!modelClass) {
    throw new Error(`[hit-base] Model "${name}" is not registered`);
  }
  return modelClass;
}

function ensureModelsRegistered() {
  if (modelsLoadStarted) {
    return;
  }
  modelsLoadStarted = true;

  // Eagerly evaluate model modules once (after FK attribute modules are defined).
  // Paths are relative to this file (models/hit/).
  const modules = import.meta.glob(
    ['./*.js', './form/Form.js', './form/FormTemplate.js', './joinList/*.js'],
    {eager: true},
  );

  const skip = new Set([
    'index',
    'modelRegistry',
    'schema',
    'validation',
    'MultiLanguageField',
  ]);
  const aliases = {
    poi: 'Poi',
  };

  for (const [path, mod] of Object.entries(modules)) {
    const file = path.split('/').pop().replace(/\.js$/, '');
    if (skip.has(file)) {
      continue;
    }
    const name = aliases[file] || file;
    if (mod?.default) {
      registerModel(name, mod.default);
    }
  }
}
